import { useTranslation } from "react-i18next";

import type { components } from "@/api/types";
import { formatDateTime, formatMoney, moneySign } from "@/shared/lib";

type TransactionItem = components["schemas"]["TransactionItem"];

/**
 * Figma: `TransactionRow` (17:126). Вариант `Type=Fee` из макета пригодился:
 * платформа удерживает долю прибыли самых доходных роботов, и это отдельная
 * строка лога (`platform_fee`), а не молчаливый вычет внутри начисления.
 * Отдельного оформления ей не нужно — сумма отрицательная, и общая раскраска
 * по знаку красит её так же, как убыток.
 *
 * `comment` приходит с бэкенда готовой русской строкой и в ru.json не
 * заводится: переводить его на клиенте нечем и незачем.
 */
export function TransactionRow({ item }: { item: TransactionItem }) {
  const { t } = useTranslation();
  const sign = moneySign(item.amount);
  const tone = sign < 0 ? "text-status-loss" : sign > 0 ? "text-status-profit" : "text-text-secondary";

  return (
    <div className="flex items-start justify-between gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-label font-semibold text-text-primary">
          {t(`wallet.txType.${item.type}`)}
        </span>
        {item.comment ? (
          <span className="truncate text-caption font-normal text-text-secondary">
            {item.comment}
          </span>
        ) : null}
        <span className="text-caption font-normal text-text-tertiary">
          {formatDateTime(item.created_at)}
        </span>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className={`text-label font-semibold ${tone}`}>
          {formatMoney(item.amount, { sign: "always" })}
        </span>
        <span className="text-caption font-normal text-text-tertiary">
          {formatMoney(item.balance_after)}
        </span>
      </div>
    </div>
  );
}
